/**
 * App — Root component with screen state machine.
 *
 * Screen routing based on wallet state + user actions.
 * Persistent: Toast container, network badge.
 */

import { useState, useCallback, useEffect, type ReactNode } from 'react';
import { APP_VERSION } from "./version.js";
import { WalletProvider, useWallet } from './modules/wallet/index.js';
import { CreateWallet } from './modules/wallet/components/CreateWallet.js';
import { ImportWallet } from './modules/wallet/components/ImportWallet.js';
import { UnlockWallet } from './modules/wallet/components/UnlockWallet.js';
import { WalletHome } from './modules/wallet/components/WalletHome.js';
import { SendScreen } from './modules/wallet/components/SendScreen.js';
import { ReceiveScreen } from './modules/wallet/components/ReceiveScreen.js';
import { TxHistory } from './modules/wallet/components/TxHistory.js';
import { SignMessage } from './modules/wallet/components/SignMessage.js';
import { BackupSeedPhrase } from './modules/wallet/components/BackupSeedPhrase.js';
import { CreateCallScreen } from './modules/qr/CreateCallScreen.js';
import { JoinCallScreen } from './modules/qr/JoinCallScreen.js';
import { CallPage } from './pages/CallPage.js';
import { StreamSettings } from './components/StreamSettings.js';
import { Toast, ToastProvider } from './components/Toast.js';
import { NetworkBadge } from './components/NetworkBadge.js';
import { getAppConfig } from './config.js';

type Screen =
  | 'loading'
  | 'welcome'
  | 'create'
  | 'import'
  | 'unlock'
  | 'home'
  | 'send'
  | 'receive'
  | 'history'
  | 'sign'
  | 'backup'
  | 'settings'
  | 'createCall'
  | 'joinCall'
  | 'inCall';

interface CallContext {
  role: 'sender' | 'receiver';
  roomId: string;
  secret: string;
  signalingUrl: string;
  peerAddress: string;
}

function AppContent(): ReactNode {
  const { status, actions } = useWallet();
  const [screen, setScreen] = useState<Screen>('loading');
  const [callContext, setCallContext] = useState<CallContext | null>(null);
  const config = getAppConfig();

  // Route based on wallet state
  useEffect(() => {
    switch (status.state) {
      case 'uninitialized':
        setScreen('welcome');
        break;
      case 'locked':
        setScreen('unlock');
        break;
      case 'unlocked':
        if (screen === 'loading' || screen === 'unlock' || screen === 'create' || screen === 'import' || screen === 'welcome') {
          setScreen('home');
        }
        break;
      case 'creating':
      case 'importing':
      case 'unlocking':
        break;
      case 'error':
        break;
    }
  }, [status.state]);

  const handleCreatorReady = useCallback((roomId: string, secret: string) => {
    setCallContext({
      role: 'sender',
      roomId,
      secret,
      signalingUrl: config.signalingUrl,
      peerAddress: '',
    });
    setScreen('inCall');
  }, [config.signalingUrl]);

  const handleJoin = useCallback((roomId: string, secret: string, signalingUrl: string, callerAddress: string) => {
    setCallContext({
      role: 'receiver',
      roomId,
      secret,
      signalingUrl,
      peerAddress: callerAddress,
    });
    setScreen('inCall');
  }, []);

  const handleCallEnd = useCallback(() => {
    setCallContext(null);
    setScreen('home');
  }, []);

  const handleLock = useCallback(() => {
    actions.lock();
  }, [actions]);

  const renderScreen = (): ReactNode => {
    switch (screen) {
      case 'loading':
        return (
          <div className="screen">
            <div className="screen-body centered">
              <div className="loading-spinner" />
              <p className="text-secondary">Loading wallet...</p>
            </div>
          </div>
        );
      case 'welcome':
        return (
          <div className="screen">
            <div className="screen-body centered">
              <h1 className="app-title">CryptoCall</h1>
              <p className="app-subtitle">Stream tokens in real-time over peer-to-peer video calls.</p>
              <p className="text-muted text-xs">v{APP_VERSION}</p>
              <div className="welcome-actions">
                <button className="btn-primary btn-full" onClick={() => setScreen('create')}>
                  Create New Wallet
                </button>
                <button className="btn-secondary btn-full" onClick={() => setScreen('import')}>
                  Import Existing Wallet
                </button>
              </div>
            </div>
          </div>
        );
      case 'create':
        return <CreateWallet onBack={() => setScreen('welcome')} />;
      case 'import':
        return <ImportWallet onBack={() => setScreen('welcome')} />;
      case 'unlock':
        return <UnlockWallet onReset={() => setScreen('import')} />;
      case 'home':
        return (
          <WalletHome
            onSend={() => setScreen('send')}
            onReceive={() => setScreen('receive')}
            onHistory={() => setScreen('history')}
            onCreateCall={() => setScreen('createCall')}
            onJoinCall={() => setScreen('joinCall')}
            onLock={handleLock}
            onBackup={() => setScreen('backup')}
            onSettings={() => setScreen('settings')}
          />
        );
      case 'send':
        return <SendScreen onBack={() => setScreen('home')} />;
      case 'receive':
        return <ReceiveScreen onBack={() => setScreen('home')} />;
      case 'history':
        return <TxHistory onBack={() => setScreen('home')} />;
      case 'sign':
        return <SignMessage onBack={() => setScreen('home')} />;
      case 'backup':
        return <BackupSeedPhrase onBack={() => setScreen('home')} />;
      case 'settings':
        return <StreamSettings onBack={() => setScreen('home')} />;
      case 'createCall':
        return (
          <CreateCallScreen
            signalingUrl={config.signalingUrl}
            chain={config.chain}
            onReady={handleCreatorReady}
            onCancel={() => setScreen('home')}
          />
        );
      case 'joinCall':
        return (
          <JoinCallScreen
            expectedChain={config.chain}
            onJoin={handleJoin}
            onCancel={() => setScreen('home')}
          />
        );
      case 'inCall':
        if (!callContext) { setScreen('home'); return null; }
        return (
          <CallPage
            role={callContext.role}
            roomId={callContext.roomId}
            secret={callContext.secret}
            signalingUrl={callContext.signalingUrl}
            peerAddress={callContext.peerAddress}
            onEnd={handleCallEnd}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="app">
      <NetworkBadge />
      {renderScreen()}
      <Toast />
    </div>
  );
}

export function App(): ReactNode {
  return (
    <ToastProvider>
      <WalletProvider>
        <AppContent />
      </WalletProvider>
    </ToastProvider>
  );
}
